import React, { useState, useEffect } from 'react'
import BreadCrumbComp from '../../../components/breadCrumb'
import SimpleTable from '../../../components/tables/simpleTable'
import SimpleSelect from '../../../components/selects/simpleSelect'
import LaddaButton from '../../../components/buttons/laddaButton'
import CustomSnackbar from '../../../components/snackbar'
import { Grid } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { quarters, years } from '../../../constants/selectData'
import { fetchTableData, fetchOrderNumbers } from './internalClientSatisfactionService'
import { internalClientSatisfactionColumns } from './columns'
import { icons } from '../../../constants'
import { isRoleUtil } from '../../../utils/AuthorizedUtils/360Report'
import { useSelector } from 'react-redux'
import GenerateSurveyLinkModal from './generateSurveyLinkModal'
import ViewSurveyResultsModal from './viewSurveyResultsModal'

const InternalClientSatisfaction = () => {
    const navigate = useNavigate()
    const [tableData, setTableData] = useState([])
    const [loading, setLoading] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [totalRecords, setTotalRecords] = useState(0)
    const [orderNumberOptions, setOrderNumberOptions] = useState([{ name: "Seleccionar", value: null }])
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')

    const today = new Date()
    const currentYear = today.getFullYear()

    const userData = useSelector((state) => state.userData.userData);
    var isExternalClientAdmin = isRoleUtil("EXTERNAL_CLIENT_ADMIN", userData.roleList);
    const [openGenerateLinkModal, setOpenGenerateLinkModal] = useState(false)
    const [openViewResultsModal, setOpenViewResultsModal] = useState(false)
    const [selectedAnswerId, setSelectedAnswerId] = useState(null)

    const yearOptions = [
        { name: "Seleccionar", value: null },
        ...Array.from({ length: 5 }, (_, i) => ({
            name: String(currentYear - i),
            value: currentYear - i
        }))
    ]

    const [appliedFilters, setAppliedFilters] = useState({
        orderNumber: null,
        quarter: null,
        year: currentYear
    })

    const {
        control,
        getValues,
        reset
    } = useForm({
        defaultValues: {
            orderNumber: null,
            quarter: null,
            year: currentYear
        }
    })

    const getTableData = async (page = 1, pageSize = 10) => {
        await fetchTableData(
            appliedFilters.orderNumber,
            appliedFilters.quarter,
            appliedFilters.year,
            page,
            pageSize,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handleFilter = () => {
        const { orderNumber, quarter, year } = getValues()
        const newFilters = {
            orderNumber: orderNumber ?? null,
            quarter: quarter ?? null,
            year: year ?? currentYear
        }
        setAppliedFilters(newFilters)
        setCurrentPage(1)
        fetchTableData(
            newFilters.orderNumber,
            newFilters.quarter,
            newFilters.year,
            1,
            rowsPerPage,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handleClearFilters = () => {
        reset({
            orderNumber: null,
            quarter: null,
            year: currentYear
        })
        const cleared = {
            orderNumber: null,
            quarter: null,
            year: currentYear
        }
        setAppliedFilters(cleared)
        setCurrentPage(1)
        fetchTableData(
            cleared.orderNumber,
            cleared.quarter,
            cleared.year,
            1,
            rowsPerPage,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handlePageChange = (page, rows) => {
        const nextPageSize = rows ?? rowsPerPage
        setCurrentPage(page)
        setRowsPerPage(nextPageSize)
        fetchTableData(
            appliedFilters.orderNumber,
            appliedFilters.quarter,
            appliedFilters.year,
            page,
            nextPageSize,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handleCopyLink = (surveyOrderId) => {
        if (surveyOrderId) {
            const surveyUrl = `${window.location.origin}/foreign_trade_board/internal_client_satisfaction/national/answer/${surveyOrderId}`
            navigator.clipboard.writeText(surveyUrl).then(() => {
                setSnackbarMessage('Se copió el link de encuesta')
                setSnackbarOpen(true)
            }).catch(() => {
                setSnackbarMessage('Error al copiar el link')
                setSnackbarOpen(true)
            })
        }
    }

    const handleViewResults = (answerId) => {
        if (answerId) {
            setSelectedAnswerId(answerId)
            setOpenViewResultsModal(true)
        }
    }

    const handleCloseViewResultsModal = () => {
        setOpenViewResultsModal(false)
        setSelectedAnswerId(null)
    }

    const handleCreateSurvey = () => {
        navigate('/foreign_trade_board/internal_client_satisfaction/create')
    }

    const handleGenerateLink = () => {
        setOpenGenerateLinkModal(true)
    }

    const handleCloseGenerateLinkModal = () => {
        setOpenGenerateLinkModal(false)
    }

    useEffect(() => {
        getTableData(currentPage, rowsPerPage)
        fetchOrderNumbers(setOrderNumberOptions)
    }, [])

    const columnsWithActions = internalClientSatisfactionColumns.map(col => {
        if (col.field === 'actions') {
            return {
                ...col,
                render: (rowData) => col.render(rowData, handleCopyLink, handleViewResults)
            }
        }
        return col
    })

    return (
        <div>
            <BreadCrumbComp />
            <div className="searchTitle">
                <h1>SATISFACCIÓN CLIENTE INTERNO</h1>
            </div>

            <div className="table-container">
                <div className="HContainer j-space-between a-center">
                    <h1 className="title-bio-details-chard">
                        SATISFACCIÓN DE CLIENTE INTERNO
                    </h1>

                    {isExternalClientAdmin && (
                        <div>
                            <LaddaButton
                                variant="contained"
                                onClick={handleCreateSurvey}
                                color={"#404040"}
                                backgroundColor={"#F4D658"}
                            >
                                <div className="IHContainer align-center">
                                    <p className="button_text">Encuesta de Satisfacción del Cliente Interno</p>
                                </div>
                            </LaddaButton>

                            <LaddaButton
                                variant="contained"
                                onClick={handleGenerateLink}
                                backgroundColor="#6C5CE7"
                            >
                                <div className="IHContainer align-center">
                                    <img
                                        className="btn-icon"
                                        src={icons.plusIcon}
                                        alt="icon"
                                        style={{ marginRight: "10px" }}
                                    />
                                    <p className="button_text">Generar enlace para orden de importación</p>
                                </div>
                            </LaddaButton>
                        </div>
                    )}
                </div>

                <div className="mb-10">
                    <form>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} md={3}>
                                <p className="info-label">Seleccionar Nro de Orden</p>
                                <SimpleSelect
                                    name="orderNumber"
                                    control={control}
                                    options={orderNumberOptions}
                                    margin={"0"}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <p className="info-label">Seleccionar Trimestre</p>
                                <SimpleSelect
                                    name="quarter"
                                    control={control}
                                    options={quarters}
                                    margin={"0"}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <p className="info-label">Seleccionar Año</p>
                                <SimpleSelect
                                    name="year"
                                    control={control}
                                    options={yearOptions}
                                    margin={"0"}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className="IHContainer">
                                    <LaddaButton
                                        variant="contained"
                                        onClick={handleFilter}
                                        margin="0"
                                    >
                                        <img
                                            className="btn-icon"
                                            src={icons.filterWhite}
                                            alt="Filtrar"
                                        />
                                    </LaddaButton>
                                    <LaddaButton
                                        variant="contained"
                                        onClick={handleClearFilters}
                                        margin="0 0 0 10px"
                                    >
                                        <img
                                            className="btn-icon"
                                            src={icons.clearFilterWhite}
                                            alt="Limpiar filtros"
                                        />
                                    </LaddaButton>
                                </div>
                            </Grid>
                        </Grid>
                    </form>
                </div>

                <SimpleTable
                    data={tableData}
                    columns={columnsWithActions}
                    isPaginated={true}
                    serverPagination={true}
                    totalRecords={totalRecords}
                    rowsPerPage={rowsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    scrollHeight="750px"
                    emptyMessage="No se han encontrado registros"
                />
            </div>

            <CustomSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity="success"
                onClose={() => setSnackbarOpen(false)}
            />

            <GenerateSurveyLinkModal
                open={openGenerateLinkModal}
                handleClose={handleCloseGenerateLinkModal}
            />

            <ViewSurveyResultsModal
                open={openViewResultsModal}
                handleClose={handleCloseViewResultsModal}
                answerId={selectedAnswerId}
            />
        </div>
    )
}

export default InternalClientSatisfaction
